import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Page,
  Card,
  Layout,
  Filters,
  EmptyState,
  Button,
  Spinner,
  Tabs,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useRestorableState from "hooks/useRestorableState";
import useIsPhone from "features/files/use-is-phone";
import { RootState, AppDispatch } from "configureStore";
import {
  fetchFuelReportsThunk,
  fetchMoreFuelReports,
  setFilterValue,
} from "./fuel-reports.slice";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import { getFuelReportTotalAmount } from "utils/fuelReportUtils";
import UploadFileToUnsave from "features/upload-to-unsave/upload-file";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import FuelTransactionsTab from "./fuel-transactions-tab";
import FuelImportResult, { toImportOutcome } from "./fuel-import-result";
import type { FuelImportOutcome } from "types/FuelImport";

const END_REACHED_THRESHOLD = 200;

function FuelReports() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const isPhone = useIsPhone();

  const { collection, filtersValue, isLoading, isLoadingMore, isError, pagination } =
    useSelector((state: RootState) => state.fuelReportReducer);

  const headerRef = useRef<HTMLDivElement>(null); // search/filters region (ResizeObserver)
  const listWrapRef = useRef<HTMLDivElement>(null); // wraps the table (measures top + hosts scroll listener)
  const sentinelRef = useRef<HTMLDivElement>(null); // infinite-scroll sentinel (phone)
  const [listMaxHeight, setListMaxHeight] = useState("calc(100vh - 280px)");
  const [selectedTab, setSelectedTab] = useRestorableState(
    "fuel-reports-tab",
    0
  );

  // What the last CSV upload did, shown above the tabs until dismissed.
  const [importOutcome, setImportOutcome] = useState<FuelImportOutcome | null>(
    null
  );
  // Bumped after an import that changed data so the transactions tab refetches.
  const [transactionsRefreshToken, setTransactionsRefreshToken] = useState(0);

  const hasNextPage = Boolean(pagination?.hasNextPage);
  const nextPage = (pagination?.page || 1) + 1;

  // A result belongs to the company it was uploaded for.
  useEffect(() => {
    setImportOutcome(null);
  }, [companyId]);

  const handleImportUploaded = useCallback(
    (data: unknown) => {
      const outcome = toImportOutcome(data);
      setImportOutcome(outcome);
      if (outcome.kind !== "result" || !companyId) return;
      const { inserted, updated } = outcome.result;
      if (Number(inserted) > 0 || Number(updated) > 0) {
        // New rows can change the amounts of existing reports, and the
        // transactions tab only reads its list when it mounts or the filter changes.
        dispatch(fetchFuelReportsThunk(companyId, filtersValue));
        setTransactionsRefreshToken((token) => token + 1);
      }
    },
    [companyId, dispatch, filtersValue]
  );

  const handleImportFailed = useCallback((message: string) => {
    setImportOutcome({ kind: "failure", message });
  }, []);

  // Initial load + reload page 1 whenever company or filters change.
  useEffect(() => {
    if (companyId) dispatch(fetchFuelReportsThunk(companyId, filtersValue));
  }, [dispatch, filtersValue, companyId]);

  // Cap the table body height so only the report rows scroll while the page
  // title, search bar and column headers stay fixed (uses DataTable's existing
  // maxBodyHeight, no component change).
  useLayoutEffect(() => {
    const compute = () => {
      const el = listWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = window.innerHeight - top - 24;
      setListMaxHeight(`${Math.max(Math.round(available), 240)}px`);
    };

    compute();
    window.addEventListener("resize", compute);

    let resizeObserver: ResizeObserver | undefined;
    if (headerRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(compute);
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", compute);
      if (resizeObserver) resizeObserver.disconnect();
    };
    // Re-run when returning to the reports tab: the list DOM is unmounted while
    // the transactions tab is active, so refs must be re-measured on remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  const loadMore = () => {
    if (companyId && hasNextPage && !isLoading && !isLoadingMore) {
      dispatch(fetchMoreFuelReports(companyId, filtersValue, nextPage));
    }
  };

  // Desktop infinite scroll: listen to the DataTable's internal scroll area
  // (the element that also makes the header sticky).
  useEffect(() => {
    if (isPhone) return undefined;
    const wrap = listWrapRef.current;
    if (!wrap) return undefined;
    const scrollEl = wrap.querySelector<HTMLElement>(".overflow-y-auto");
    if (!scrollEl) return undefined;

    const onScroll = () => {
      if (isLoading || isLoadingMore || !hasNextPage) return;
      if (
        scrollEl.scrollTop + scrollEl.clientHeight >=
        scrollEl.scrollHeight - END_REACHED_THRESHOLD
      ) {
        loadMore();
      }
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // auto-fill if the first page doesn't fill the viewport
    return () => scrollEl.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    filtersValue,
    hasNextPage,
    isLoading,
    isLoadingMore,
    collection.length,
    selectedTab,
    isPhone,
  ]);

  // Phone infinite scroll: rows flow in the page, so observe a sentinel
  // against the viewport.
  useEffect(() => {
    if (!isPhone) return undefined;
    const target = sentinelRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    filtersValue,
    hasNextPage,
    isLoading,
    isLoadingMore,
    selectedTab,
    isPhone,
  ]);

  // Search/filter logic
  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };
  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };
  const handleClearAll = () => {};
  const appliedFilters: any = [];
  if (filtersValue.textSearch && filtersValue.textSearch.length > 0) {
    appliedFilters.push({
      key: "textSearch",
      label: `${t("search")}: ${filtersValue.textSearch}`,
      onRemove: handleQueryValueRemove,
    });
  }

  const filters = [
    {
      key: "",
      label: "",
      filter: <></>,
    },
  ];
  const filtersNode = (
    <Filters
      queryValue={filtersValue.textSearch}
      filters={filters}
      appliedFilters={appliedFilters}
      onQueryChange={handleQueryChange}
      onQueryClear={handleQueryValueRemove}
      onClearAll={handleClearAll}
      hideFilters
    />
  );

  // Map the collection to table rows
  const fuelReportsTableData =
    collection?.map((item: any, idx: number) => {
      // Handle fuelCard as array or single value
      let fuelCardDisplay = "";
      if (Array.isArray(item.fuelCards)) {
        fuelCardDisplay = item.fuelCards
          .map((card: any) => card?.name || "")
          .filter(Boolean)
          .join(", ");
      }

      const assetObj = typeof item.asset === "object" ? item.asset : {};
      return {
        id: item.id,
        "#": idx + 1,
        user: typeof item.user === "object" ? item.user.name : item.user,
        period: item.period,
        asset: typeof item.asset === "object" ? item.asset.name : item.asset,
        fuelType: assetObj.fuelType || "-",
        fuelCard: fuelCardDisplay,
        endOfMonthKm: item.endOfMonthKm,
        endOfMonthLtr: item.endOfMonthLtr,
        amount: getFuelReportTotalAmount(item),
      };
    }) || [];

  const handleRowClick = (item: any) => {
    if (item.id && companyId) {
      navigate(`/app/${companyId}/fuel-reports/${item.id}`);
    }
  };

  // On a phone the whole card opens the report (rowUrl below); on wider
  // screens the cell texts do.
  const openReport = isPhone ? undefined : handleRowClick;

  const columns = [
    { key: "#" },
    {
      key: "period",
      name: t("date"),
      onClick: openReport,
      mobileView: "topLeft",
    },
    {
      key: "user",
      name: t("user"),
      onClick: openReport,
      mobileView: "bottomLeft",
    },
    {
      key: "asset",
      name: t("asset"),
      onClick: openReport,
      mobileView: "bottomRight",
    },
    { key: "fuelType", name: t("fuel_type"), onClick: openReport },
    { key: "fuelCard", name: t("fuel_card"), onClick: openReport },
    {
      key: "amount",
      type: "currency",
      name: t("amount"),
      onClick: openReport,
      mobileView: "topRight",
    },
  ] as Column[];

  const showSkeleton = isLoading && fuelReportsTableData.length === 0;
  const showEmpty = !isLoading && !isError && fuelReportsTableData.length === 0;

  const tabs = [
    { id: "fuel-reports", content: t("fuel_reports") },
    { id: "uploaded-transactions", content: t("uploaded_transactions") },
  ];

  const reportsListBody = (
    <Card>
      <div>
        <div ref={headerRef} style={{ padding: "16px", display: "flex" }}>
          <div style={{ flex: 1 }}>{filtersNode}</div>
        </div>
        <div ref={listWrapRef}>
          {showSkeleton ? (
            <DataTableSkeleton rows={10} columns={columns.length} />
          ) : isError ? (
            <EmptyState
              heading={t("not_fetch", {
                defaultValue: "Could not load fuel reports",
              })}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <Button
                onClick={() =>
                  companyId &&
                  dispatch(fetchFuelReportsThunk(companyId, filtersValue))
                }
              >
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </EmptyState>
          ) : showEmpty ? (
            <EmptyState
              heading={t("no_data", {
                defaultValue: "No fuel reports found",
              })}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          ) : (
            <>
              <DataTable
                collection={fuelReportsTableData}
                columns={columns}
                rowUrl={
                  isPhone
                    ? (item) => `/app/${companyId}/fuel-reports/${item.id}`
                    : undefined
                }
                maxBodyHeight={isPhone ? undefined : listMaxHeight}
              />
              {/* Sentinel for phone infinite scroll (page-level scroll) */}
              <div ref={sentinelRef} style={{ height: 1 }} />
            </>
          )}
        </div>
      </div>
    </Card>
  );

  const content = (
    <Page
      fullWidth
      primaryAction={
        <UploadFileToUnsave
          label={t("upload_fuel_report")}
          onUploaded={handleImportUploaded}
          onUploadError={handleImportFailed}
        />
      }
      title={t("fuel_reports")}
      titleMetadata={
        <>
          <AuditLogPopover subjectType="FuelReport" />
          {isLoadingMore && (
            <Spinner
              accessibilityLabel={t("loading", { defaultValue: "Loading" })}
              size="small"
            />
          )}
        </>
      }
    >
      {importOutcome && (
        <div className="mb-4">
          <FuelImportResult
            outcome={importOutcome}
            onDismiss={() => setImportOutcome(null)}
          />
        </div>
      )}
      <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
      <div style={{ marginTop: "16px" }}>
        {selectedTab === 0 ? (
          reportsListBody
        ) : (
          <FuelTransactionsTab refreshToken={transactionsRefreshToken} />
        )}
      </div>
    </Page>
  );

  return (
    <Layout>
      <Layout.Section>{content}</Layout.Section>
    </Layout>
  );
}

export default FuelReports;
